import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, ArrowLeft, Crown, Medal, Clock, Users, Zap, Lock, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// V5 Design Constants
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    orangeDark: '#ea580c',
    green: '#22c55e',
    amber: '#f59e0b',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
    borderDefault: 'rgba(30, 27, 75, 0.1)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

// ============================================
// DYNAMIC BACKGROUND - Option 3: Gradient Mesh + Corner Accents
// ============================================
const GradientMeshBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    {/* Main gradient mesh */}
    <div 
      className="absolute inset-0"
      style={{
        background: `
          radial-gradient(at 0% 0%, rgba(139, 92, 246, 0.08) 0%, transparent 40%),
          radial-gradient(at 100% 0%, rgba(249, 115, 22, 0.06) 0%, transparent 40%),
          radial-gradient(at 100% 100%, rgba(245, 158, 11, 0.08) 0%, transparent 40%),
          radial-gradient(at 0% 100%, rgba(139, 92, 246, 0.06) 0%, transparent 40%)
        `,
      }}
    />
    
    {/* Top-left corner accent */}
    <motion.div
      animate={{
        opacity: [0.4, 0.6, 0.4],
        scale: [1, 1.05, 1],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
      }}
      className="absolute top-0 left-0 w-64 h-64"
      style={{
        background: `linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, transparent 60%)`,
      }}
    />
    
    {/* Top-right corner accent */}
    <motion.div
      animate={{
        opacity: [0.3, 0.5, 0.3],
        scale: [1, 1.08, 1],
      }}
      transition={{
        duration: 10,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 2,
      }}
      className="absolute top-0 right-0 w-72 h-72"
      style={{
        background: `linear-gradient(225deg, rgba(249, 115, 22, 0.12) 0%, transparent 60%)`,
      }}
    />
    
    {/* Bottom-right corner accent */}
    <motion.div
      animate={{
        opacity: [0.35, 0.55, 0.35],
        scale: [1, 1.06, 1],
      }}
      transition={{
        duration: 9,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 4,
      }}
      className="absolute bottom-0 right-0 w-80 h-80"
      style={{
        background: `linear-gradient(315deg, rgba(245, 158, 11, 0.1) 0%, transparent 60%)`,
      }}
    />
    
    {/* Bottom-left corner accent */}
    <motion.div
      animate={{
        opacity: [0.3, 0.45, 0.3],
        scale: [1, 1.04, 1],
      }}
      transition={{
        duration: 11,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 6,
      }}
      className="absolute bottom-0 left-0 w-56 h-56"
      style={{
        background: `linear-gradient(45deg, rgba(139, 92, 246, 0.1) 0%, transparent 60%)`,
      }}
    />
    
    {/* Animated diagonal line accent */}
    <motion.div
      animate={{
        x: [-100, 100],
        opacity: [0, 0.1, 0],
      }}
      transition={{
        duration: 15,
        repeat: Infinity,
        ease: "linear",
      }}
      className="absolute top-1/2 left-0 right-0 h-px"
      style={{
        background: `linear-gradient(90deg, transparent, ${v5.colors.purple}, transparent)`,
        transform: 'rotate(-5deg)',
      }}
    />
  </div>
);

const Leaderboard = () => {
  const { hubId } = useParams();
  const { isAuthenticated, loading: authLoading, openAuthModal } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (isAuthenticated) {
      fetchLeaderboard();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, hubId]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${BACKEND_URL}/api/leaderboard/${hubId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLeaderboard(response.data);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      if (err.response?.status === 403) {
        setError('unlock');
      } else {
        setError('Failed to load leaderboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6" style={{ color: v5.colors.amber }} />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Medal className="w-6 h-6" style={{ color: '#cd7f32' }} />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center font-bold" style={{ color: v5.colors.textMuted }}>{rank}</span>;
    }
  };

  const getRankBg = (rank, isCurrentUser) => {
    if (isCurrentUser) {
      return { 
        background: 'linear-gradient(to right, rgba(249, 115, 22, 0.1), rgba(245, 158, 11, 0.1))',
        border: `2px solid ${v5.colors.orange}`
      };
    }
    switch (rank) {
      case 1:
        return { 
          background: 'linear-gradient(to right, rgba(245, 158, 11, 0.1), rgba(251, 191, 36, 0.1))',
          border: `1px solid rgba(245, 158, 11, 0.2)`
        };
      case 2:
        return { 
          background: 'linear-gradient(to right, rgba(148, 163, 184, 0.1), rgba(203, 213, 225, 0.1))',
          border: `1px solid rgba(148, 163, 184, 0.2)`
        };
      case 3:
        return { 
          background: 'linear-gradient(to right, rgba(249, 115, 22, 0.05), rgba(245, 158, 11, 0.05))',
          border: `1px solid rgba(249, 115, 22, 0.2)`
        };
      default:
        return { 
          background: v5.colors.white,
          border: `1px solid ${v5.colors.borderLight}`
        };
    }
  };

  const getPrizeAmount = (rank) => {
    switch (rank) {
      case 1: return '$100';
      case 2: return '$50';
      case 3: return '$25';
      default: return null;
    }
  };

  // Loading state
  if (authLoading || (isAuthenticated && loading)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: v5.colors.cream }}>
        <div 
          className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: v5.colors.purple, borderTopColor: 'transparent' }}
        ></div>
      </div>
    );
  }

  // Unauthenticated state
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: v5.colors.cream }}>
        {/* V5 Hero Section */}
        <HeroSection hubName="Hub" userRank={null} userTaps={0} totalParticipants={0} daysRemaining={0} />

        {/* Login Prompt */}
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl p-8"
            style={{ 
              backgroundColor: v5.colors.white,
              border: `1px solid ${v5.colors.borderLight}`,
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
              borderLeft: `4px solid ${v5.colors.orange}`
            }}
          >
            <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '16px' }}>
              // sign_in_required
            </p>
            <div 
              className="w-20 h-20 mx-auto mb-6 rounded-xl flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${v5.colors.amber} 0%, ${v5.colors.orange} 100%)` }}
            >
              <Crown className="w-10 h-10 text-white" />
            </div>
            <h2 style={{ fontFamily: v5.fonts.display, fontSize: '24px', fontWeight: 500, color: v5.colors.textPrimary, marginBottom: '12px' }}>
              View Leaderboard
            </h2>
            <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted, marginBottom: '24px' }}>
              Sign in to see where you rank and compete for monthly prizes at this hub!
            </p>
            
            {/* Prize Preview */}
            <div 
              className="rounded-xl p-4 mb-6"
              style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}
            >
              <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', fontWeight: 600, color: v5.colors.textPrimary, marginBottom: '8px' }}>
                Monthly Prizes
              </p>
              <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textSecondary }}>
                🥇 $100 • 🥈 $50 • 🥉 $25
              </p>
            </div>

            <button
              onClick={() => openAuthModal('login')}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:-translate-y-0.5"
              style={{ 
                background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.orangeDark} 100%)`,
                fontFamily: v5.fonts.body,
                boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
              }}
            >
              <LogIn className="w-5 h-5" />
              Sign In to View
            </button>
            
            <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted, marginTop: '16px' }}>
              Don&apos;t have an account?{' '}
              <button 
                onClick={() => openAuthModal('signup')}
                style={{ color: v5.colors.orange, fontWeight: 600 }}
                className="hover:underline"
              >
                Sign up free
              </button>
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Hub not unlocked state
  if (error === 'unlock') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: v5.colors.cream }}>
        {/* V5 Hero Section */}
        <HeroSection hubName="Hub Locked" userRank={null} userTaps={0} totalParticipants={0} daysRemaining={0} />
        
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div 
            className="w-20 h-20 mx-auto mb-6 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: 'rgba(30, 27, 75, 0.1)' }}
          >
            <Lock className="w-10 h-10" style={{ color: v5.colors.textHint }} />
          </div>
          <h1 style={{ fontFamily: v5.fonts.display, fontSize: '24px', fontWeight: 500, color: v5.colors.textPrimary, marginBottom: '16px' }}>
            Hub Not Unlocked
          </h1>
          <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted, marginBottom: '24px' }}>
            You need to scan a coaster at this hub to view its leaderboard.
          </p>
          <Link
            to="/rewards"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:-translate-y-0.5"
            style={{ 
              background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
              fontFamily: v5.fonts.body,
              boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
            }}
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Rewards
          </Link>
        </div>
      </div>
    );
  }

  // Main leaderboard view
  return (
    <div className="min-h-screen" style={{ backgroundColor: v5.colors.cream }}>
      {/* V5 Hero Section */}
      <HeroSection 
        hubName={leaderboard?.hub_name || 'Hub'}
        userRank={leaderboard?.user_rank}
        userTaps={leaderboard?.user_taps || 0}
        totalParticipants={leaderboard?.total_participants || 0}
        daysRemaining={leaderboard?.days_remaining || 0}
        monthKey={leaderboard?.month_key}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Prize Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-xl p-6 text-white"
          style={{ background: `linear-gradient(135deg, ${v5.colors.amber} 0%, ${v5.colors.orange} 100%)` }}
        >
          <div className="flex items-center gap-4">
            <Trophy className="w-10 h-10" />
            <div>
              <h3 style={{ fontFamily: v5.fonts.body, fontWeight: 700, fontSize: '18px' }}>Monthly Prizes</h3>
              <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                🥇 $100 • 🥈 $50 • 🥉 $25 gift cards
              </p>
            </div>
          </div>
        </motion.div>

        {/* Leaderboard List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl p-6"
          style={{ 
            backgroundColor: v5.colors.white,
            border: `1px solid ${v5.colors.borderLight}`,
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
            borderLeft: `4px solid ${v5.colors.purple}`
          }}
        >
          <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '12px' }}>
            // top_players
          </p>
          <div className="flex items-center justify-between mb-6">
            <h2 className="flex items-center gap-2" style={{ fontFamily: v5.fonts.display, fontSize: '20px', fontWeight: 500, color: v5.colors.textPrimary }}>
              <Users className="w-5 h-5" style={{ color: v5.colors.purple }} />
              Top 10
            </h2>
            <span style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted }}>
              {leaderboard?.total_participants || 0} participants
            </span>
          </div>

          {(!leaderboard?.entries || leaderboard.entries.length === 0) ? (
            <div className="text-center py-8">
              <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}>
                No participants yet. Be the first to earn Taps at this hub!
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.entries.map((entry, idx) => {
                const rankStyle = getRankBg(entry.rank, entry.is_current_user);
                return (
                  <motion.div
                    key={entry.user_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * idx }}
                    className="flex items-center justify-between p-4 rounded-xl transition-all"
                    style={rankStyle}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center">
                        {getRankIcon(entry.rank)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.textPrimary }}>
                            {entry.display_name}
                            {entry.is_current_user && (
                              <span 
                                className="ml-2 text-xs px-2 py-0.5 rounded-full text-white"
                                style={{ backgroundColor: v5.colors.orange }}
                              >
                                You
                              </span>
                            )}
                          </p>
                        </div>
                        {!entry.leaderboard_eligible && (
                          <p style={{ fontFamily: v5.fonts.body, fontSize: '12px', color: v5.colors.amber }}>
                            Not eligible for prizes
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {getPrizeAmount(entry.rank) && (
                        <span 
                          className="px-3 py-1 text-sm font-bold rounded-full text-white"
                          style={{ background: `linear-gradient(135deg, ${v5.colors.amber} 0%, ${v5.colors.orange} 100%)` }}
                        >
                          {getPrizeAmount(entry.rank)}
                        </span>
                      )}
                      <div className="text-right min-w-[80px]">
                        <p style={{ fontFamily: v5.fonts.body, fontWeight: 700, fontSize: '18px', color: v5.colors.textPrimary }}>
                          {entry.total_taps}
                        </p>
                        <p style={{ fontFamily: v5.fonts.body, fontSize: '12px', color: v5.colors.textMuted }}>Taps</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* How to Climb */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-xl p-6 text-white"
          style={{ backgroundColor: v5.colors.navy }}
        >
          <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: 'rgba(255, 255, 255, 0.4)', marginBottom: '12px' }}>
            // earn_taps
          </p>
          <h3 style={{ fontFamily: v5.fonts.display, fontWeight: 500, marginBottom: '16px' }}>
            💡 How to Climb the Leaderboard
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm" style={{ fontFamily: v5.fonts.body }}>
            <div className="flex items-center gap-2">
              <span style={{ color: v5.colors.orange }}>+10</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Daily scan</span>
            </div>
            <div className="flex items-center gap-2">
              <span style={{ color: v5.colors.orange }}>+25</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Claim an offer</span>
            </div>
            <div className="flex items-center gap-2">
              <span style={{ color: v5.colors.orange }}>+50</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Redeem an offer</span>
            </div>
            <div className="flex items-center gap-2">
              <span style={{ color: v5.colors.orange }}>+200</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Monthly quest</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============================================
// HERO SECTION COMPONENT
// ============================================
const HeroSection = ({ hubName, userRank, userTaps, totalParticipants, daysRemaining, monthKey }) => {
  // Code snippets for the grid background
  const codeSnippets = [
    '{ rank: #1 }',
    'compete()',
    '// leaderboard',
    'taps++',
    '=> prize',
    'win()',
  ];

  return (
    <section className="relative overflow-hidden" style={{ paddingTop: '8rem' }}>
      {/* Navy background */}
      <div style={{ 
        position: 'absolute', 
        inset: 0, 
        backgroundColor: v5.colors.navy,
        boxShadow: '0 4px 20px rgba(30, 27, 75, 0.3)'
      }}></div>
      
      {/* Code Grid Pattern - Inside Hero */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Subtle grid pattern - 10% more visible */}
        <div 
          className="absolute inset-0 opacity-[0.09]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
        
        {/* Floating code snippets */}
        {codeSnippets.map((snippet, index) => (
          <motion.div
            key={index}
            animate={{ 
              opacity: [0.1, 0.25, 0.1],
              y: [0, -15, 0],
            }}
            transition={{
              duration: 6 + index * 1.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 1,
            }}
            className="absolute text-xs whitespace-nowrap"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              color: index % 2 === 0 ? 'rgba(139, 92, 246, 0.5)' : 'rgba(249, 115, 22, 0.5)',
              top: `${15 + (index * 15) % 70}%`,
              left: `${5 + (index * 18) % 85}%`,
              fontSize: '11px',
            }}
          >
            {snippet}
          </motion.div>
        ))}
        
        {/* Gradient accents */}
        <div 
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 20% 30%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
                         radial-gradient(ellipse at 80% 70%, rgba(249, 115, 22, 0.12) 0%, transparent 50%)`,
          }}
        />
      </div>
      
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 md:py-20 text-center">
      {/* Back Link */}
      <Link 
        to="/rewards" 
        className="inline-flex items-center gap-2 mb-6 transition-colors"
        style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: v5.fonts.body }}
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Rewards
      </Link>
      
      {/* Code accent */}
      <motion.p 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
        style={{ 
          fontFamily: v5.fonts.mono,
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.4)'
        }}
      >
        // hub_leaderboard
      </motion.p>
      
      {/* Badge with Time */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="inline-flex items-center gap-4 px-4 py-2 rounded-full mb-6"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
      >
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5" style={{ color: v5.colors.amber }} />
          <span 
            className="font-medium"
            style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: v5.fonts.body }}
          >
            {monthKey ? new Date(monthKey + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'This Month'}
          </span>
        </div>
        <div className="w-px h-5" style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}></div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
          <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontFamily: v5.fonts.body, fontSize: '14px' }}>
            {daysRemaining} days left
          </span>
        </div>
      </motion.div>
      
      {/* Title */}
      <motion.h1 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-4xl md:text-5xl lg:text-6xl mb-4"
        style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.white }}
      >
        {hubName} Leaderboard
      </motion.h1>
      <motion.p 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-lg max-w-md mx-auto mb-8"
        style={{ fontFamily: v5.fonts.body, color: 'rgba(255, 255, 255, 0.6)' }}
      >
        Compete for monthly prizes at this location
      </motion.p>
      
      {/* Quick Stats */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex items-center justify-center gap-6 md:gap-10"
      >
        <div className="text-center">
          <div 
            className="text-3xl md:text-4xl font-bold"
            style={{ color: v5.colors.orange, fontFamily: v5.fonts.body }}
          >
            {userRank ? `#${userRank}` : '-'}
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', fontFamily: v5.fonts.body }}>Your Rank</div>
        </div>
        <div className="w-px h-12" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}></div>
        <div className="text-center">
          <div 
            className="text-3xl md:text-4xl font-bold"
            style={{ color: v5.colors.green, fontFamily: v5.fonts.body }}
          >
            {userTaps}
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', fontFamily: v5.fonts.body }}>Your Taps</div>
        </div>
        <div className="w-px h-12" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}></div>
        <div className="text-center">
          <div 
            className="text-3xl md:text-4xl font-bold"
            style={{ color: v5.colors.amber, fontFamily: v5.fonts.body }}
          >
            {totalParticipants}
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', fontFamily: v5.fonts.body }}>Players</div>
        </div>
      </motion.div>
    </div>
    
    {/* V5 Purple-Orange Accent Line */}
    <div 
      className="absolute bottom-0 left-0 w-full h-1" 
      style={{ background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)` }} 
    />
  </section>
  );
};

export default Leaderboard;
